// bombard_cannon_<colour>.png is an 8x7 grid of 256px cells:
//   column = facing, in 45 degree steps starting at east and going clockwise
//            (0=E, 1=SE, 2=S, 3=SW, 4=W, 5=NW, 6=N, 7=NE)
//   row    = frame of the push cycle
// Because every facing is drawn, nothing needs mirroring.
import { UNIT_DRAW_SIZE } from "../../constants/GameConstants";

// Cell size in the sheet. Drawn at UNIT_DRAW_SIZE, not at this size.
const SOURCE_CELL = 256;
const FACING_COUNT = 8;
// Rows 0-3 are the four-frame walk cycle, rows 4-6 the idle breath.
const PUSH_ROWS = 4;
const IDLE_ROWS = 3;
const PUSH_FRAME_MS = 165;
// Idle cycles far slower — breathing, not marching.
const IDLE_FRAME_MS = 430;
const EAST = 0;

// How long after the last position change a unit keeps animating. The server
// only moves units a few times a second, so without this the sprite would snap
// back to the idle frame between updates and look like it was stuttering.
export const WALK_LINGER_MS = 500;

// Which of the 8 drawn facings best matches the direction of travel. A unit
// that isn't moving keeps the way it was already pointing.
export const facingOf = (dx, dy, previousFacing) => {
  if (dx === 0 && dy === 0) {
    return previousFacing;
  }
  const step = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  return (step + FACING_COUNT) % FACING_COUNT;
};

// phaseMs staggers each unit's idle cycle so a group doesn't breathe in
// lockstep, which reads as mechanical.
export const spriteCell = (isMoving, timeMs, facing, phaseMs = 0) => {
  if (isMoving) {
    return {
      col: facing,
      row: Math.floor(timeMs / PUSH_FRAME_MS) % PUSH_ROWS,
    };
  }
  const breath = Math.floor((timeMs + phaseMs) / IDLE_FRAME_MS) % IDLE_ROWS;
  return { col: facing, row: PUSH_ROWS + breath };
};

// Ground shadow, drawn on the canvas rather than baked into the sprite so it
// stays planted while the body breathes above it — that contrast is most of
// what sells a unit as a solid object instead of a flat decal.
// Three stacked ellipses rather than a blur filter: no per-frame allocation
// and no dependence on ctx.filter support.
const SHADOW_LAYERS = [
  [1.0, 0.1],
  [0.78, 0.13],
  [0.54, 0.16],
];

// The art puts the wheels below the cell centre, so the shadow sits low.
// All three scale with the draw size.
const SHADOW_OFFSET_Y = UNIT_DRAW_SIZE * 0.18;
const SHADOW_RX = UNIT_DRAW_SIZE * 0.29;
const SHADOW_RY = UNIT_DRAW_SIZE * 0.095;

export const drawUnitShadow = (ctx, x, y) => {
  const centreY = y + SHADOW_OFFSET_Y;
  for (const [scale, alpha] of SHADOW_LAYERS) {
    ctx.fillStyle = `rgba(18, 22, 12, ${alpha})`;
    ctx.beginPath();
    ctx.ellipse(x, centreY, SHADOW_RX * scale, SHADOW_RY * scale, 0, 0, 2 * Math.PI);
    ctx.fill();
  }
};

export const INITIAL_FACING = EAST;

// x,y is the unit's position and the sprite is centred on it, matching the
// marker drawn by drawDot.
export const drawSprite = (ctx, img, x, y, cell) => {
  const half = UNIT_DRAW_SIZE / 2;
  ctx.drawImage(
    img,
    cell.col * SOURCE_CELL,
    cell.row * SOURCE_CELL,
    SOURCE_CELL,
    SOURCE_CELL,
    x - half,
    y - half,
    UNIT_DRAW_SIZE,
    UNIT_DRAW_SIZE
  );
};

// TODO: For projectiles, get a direct line path, then
// just draw an image that gets taller and taller as
// the projectile goes uo in the air. Calc the peak of
// the line, then start descending the bomb
